// Financial review queue — banded by what it asks of you.
// Oct 3 2026 (Randy, audit §6). Ten kinds of money event used to share one
// ritual: expand, read twelve monospace tiles, choose at least one of five
// consequences, type a mandatory note, press Resolve. Four of them need a
// judgement, two need an action in Stripe under a deadline, and four are
// receipts. Now they are grouped that way, they lead with the money in words,
// the identifiers live behind Technical details, and a receipt closes in one
// tap.
import { useState } from "react";
import {
  CaretDown, CaretRight, CheckCircle, Warning, Clock, Receipt, ArrowSquareOut,
  ArrowUUpLeft,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  fmt$, KIND_LABEL, BANDS, deadlineInfo, DEADLINE_STYLE, moneyStory, alreadyDone,
  groupIncidents,
} from "@/lib/financialReview";

const ACTION_LABEL = {
  platform_loss: "Record as platform loss",
  restrict_voucher_purchases: "Pause voucher purchases",
  restrict_flush: "Pause cash withdrawal",
  suspend_for_review: "Suspend account for review",
  note_only: "Internal note only",
};

const BAND_ICON = { warning: Warning, clock: Clock, receipt: Receipt };

export default function FinancialReviewQueue({ data, showResolved, onToggleResolved,
                                               onChanged, onRefundCharge, dashboardBase }) {
  const incidents = groupIncidents(data?.items || []);
  const byKind = data?.open_by_kind || {};
  const total = data?.open_total_cents || 0;

  return (
    <div className="space-y-3" data-testid="voucher-review-queue">
      <div className="flex items-baseline gap-3 flex-wrap">
        <div className="text-xs font-medium text-[#1C1B1A]">Review queue</div>
        <div className="text-[10px] text-[#6B6A68]">
          {Object.keys(byKind).length
            ? `${fmt$(total)} on the table across ${Object.values(byKind).reduce((a, v) => a + v.count, 0)} open item(s)`
            : "Nothing open. Refunds inside unused credit, and disputes with nothing spent, resolve themselves and never land here."}
        </div>
        <button
          type="button" onClick={onToggleResolved}
          data-testid="voucher-review-toggle-resolved"
          className="ml-auto text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded border border-[#D8D6D2] text-[#6B6A68] hover:border-[#A89360] hover:text-[#1C1B1A]"
        >
          {showResolved ? "Open only" : "Include resolved"}
        </button>
      </div>

      {BANDS.map((band) => (
        <Band
          key={band.id} band={band}
          incidents={incidents.filter((i) => i.band === band.id)}
          onChanged={onChanged} onRefundCharge={onRefundCharge} dashboardBase={dashboardBase}
        />
      ))}
    </div>
  );
}

function Band({ band, incidents, onChanged, onRefundCharge, dashboardBase }) {
  const openCount = incidents.reduce((a, i) => a + i.openCount, 0);
  const [open, setOpen] = useState(band.id !== "record" || openCount > 0);
  const Icon = BAND_ICON[band.icon];
  const cents = incidents.reduce(
    (a, i) => a + i.items.filter((x) => x.status === "open")
      .reduce((b, x) => b + (x.amount_cents || 0), 0), 0);

  if (!incidents.length) {
    return (
      <div className="bg-white border hairline rounded-lg px-4 py-2.5 flex items-center gap-3"
           data-testid={`review-band-${band.id}-empty`}>
        <Icon size={14} weight="duotone" className="text-[#A9A7A2] shrink-0" />
        <span className="text-[11px] text-[#6B6A68]">{band.label}</span>
        <span className="ml-auto text-[10px] text-[#A9A7A2] italic">nothing here</span>
      </div>
    );
  }

  return (
    <div className="bg-white border hairline rounded-lg overflow-hidden"
         data-testid={`review-band-${band.id}`}>
      <button
        type="button" onClick={() => setOpen((v) => !v)}
        data-testid={`review-band-toggle-${band.id}`}
        className={`w-full px-4 py-3 flex items-center gap-3 text-left border-b hairline ${band.head}`}
      >
        {open ? <CaretDown size={12} weight="bold" className="text-[#6B6A68] shrink-0" />
              : <CaretRight size={12} weight="bold" className="text-[#6B6A68] shrink-0" />}
        <Icon size={15} weight="duotone" className="text-[#6B6A68] shrink-0" />
        <span className="min-w-0">
          <span className="block text-xs font-medium text-[#1C1B1A]">
            {band.label}
            <span className="ml-2 text-[10px] text-[#6B6A68] font-normal">
              {incidents.length} {incidents.length === 1 ? "entry" : "entries"}
              {cents ? ` · ${fmt$(cents)}` : ""}
            </span>
          </span>
          <span className="block text-[10px] text-[#6B6A68]">{band.blurb}</span>
        </span>
      </button>
      {open && (
        <div className="divide-y hairline">
          {incidents.map((inc) => (
            <Incident key={inc.key} incident={inc} onChanged={onChanged}
                      onRefundCharge={onRefundCharge} dashboardBase={dashboardBase} />
          ))}
        </div>
      )}
    </div>
  );
}

function Incident({ incident, onChanged, onRefundCharge, dashboardBase }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const p = incident.primary;
  const dl = deadlineInfo(p.extra?.evidence_due_by);
  const multi = incident.items.length > 1;

  // One tap means one tap: a receipt files from the collapsed row, without
  // making anyone open it to find the button.
  const acknowledgeAll = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      for (const it of incident.items.filter((x) => x.status === "open")) {
        await api.post(`/admin/vouchers/review/${it.id}/acknowledge`, {});
      }
      toast.success("Filed. Nothing was decided and nothing moved.");
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't file it.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid={`review-incident-${incident.key}`}>
      <button
        type="button" onClick={() => setOpen((v) => !v)}
        data-testid={`voucher-review-expand-${p.id}`}
        className="w-full px-4 py-3 flex items-start gap-3 text-left hover:bg-[#FAF9F5]"
      >
        {open ? <CaretDown size={12} weight="bold" className="text-[#6B6A68] shrink-0 mt-1" />
              : <CaretRight size={12} weight="bold" className="text-[#6B6A68] shrink-0 mt-1" />}
        <span className="flex-1 min-w-0">
          <span className="block text-[11px] text-[#1C1B1A] leading-snug"
                data-testid={`review-story-${p.id}`}>
            {moneyStory(p)}
          </span>
          <span className="block text-[10px] text-[#6B6A68] mt-1">
            {KIND_LABEL[p.kind] || p.kind} · {new Date(p.created_at).toLocaleDateString(
              undefined, { month: "short", day: "numeric" })}
            {multi ? ` · ${incident.items.length} events on this one payment` : ""}
          </span>
          {dl && (
            <span className={`inline-block mt-1 text-[9px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded border ${DEADLINE_STYLE[dl.tone]}`}
                  data-testid={`voucher-review-deadline-${p.id}`}>
              {dl.label}
            </span>
          )}
          {incident.band === "clock" && !dl && (
            <span className="inline-block mt-1 text-[9px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded border border-[#D8D6D2] text-[#6B6A68] bg-[#FAF9F5]"
                  data-testid={`voucher-review-deadline-${p.id}`}>
              No deadline recorded — check the Stripe Dashboard
            </span>
          )}
        </span>
        <span className="font-mono tabular-nums text-sm text-[#7A3A2A] shrink-0">
          {fmt$(p.amount_cents)}
        </span>
        {incident.band === "record" && incident.openCount > 0 ? (
          <span
            role="button" tabIndex={0} onClick={acknowledgeAll}
            onKeyDown={(e) => e.key === "Enter" && acknowledgeAll(e)}
            data-testid={`review-acknowledge-row-${p.id}`}
            className="text-[9px] uppercase tracking-[0.16em] px-2 py-0.5 rounded border border-[#4A5D4E] bg-[#EEF3EE] text-[#2F4A33] hover:bg-[#E4EDE5] shrink-0"
          >
            {busy ? "Filing…" : "Acknowledge"}
          </span>
        ) : (
          <span className={`text-[9px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded border shrink-0 ${
            incident.openCount ? "border-[#D9A97A] text-[#8A5A00] bg-[#FDF8EE]"
                               : "border-[#B7CBB9] text-[#2F6B3A] bg-[#F1F7F1]"}`}
                data-testid={`voucher-review-status-${p.id}`}>
            {incident.openCount ? "open" : "resolved"}
          </span>
        )}
      </button>

      {open && (
        <div className="px-4 pb-4 bg-[#FAF9F5] border-t hairline"
             data-testid={`voucher-review-detail-${p.id}`}>
          {multi && (
            <div className="flex flex-wrap gap-1.5 py-3" data-testid={`review-stamps-${incident.key}`}>
              {incident.items.map((it) => (
                <span key={it.id}
                      className={`text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded border ${
                        it.status === "open" ? "border-[#D9A97A] text-[#8A5A00] bg-[#FDF8EE]"
                                             : "border-[#B7CBB9] text-[#2F6B3A] bg-[#F1F7F1]"}`}>
                  {new Date(it.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  {" · "}{KIND_LABEL[it.kind] || it.kind}
                </span>
              ))}
            </div>
          )}
          <div className="space-y-3 pt-1">
            {incident.items.map((it) => (
              <ReviewItem key={it.id} item={it} onChanged={onChanged}
                          onRefundCharge={onRefundCharge} dashboardBase={dashboardBase}
                          solo={!multi} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ReviewItem({ item, onChanged, onRefundCharge, dashboardBase, solo }) {
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");
  const [actions, setActions] = useState(["platform_loss"]);
  const [busy, setBusy] = useState(false);
  const isOpen = item.status === "open";

  const loadDetail = async () => {
    if (detail) return;
    try {
      const r = await api.get(`/admin/vouchers/review/${item.id}`);
      setDetail(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load the technical detail.");
    }
  };

  const toggleAction = (a) =>
    setActions((prev) => (prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]));

  const resolve = async () => {
    if (!note.trim()) { toast.error("A resolution note is required."); return; }
    if (!actions.length) { toast.error("Pick at least one action."); return; }
    setBusy(true);
    try {
      await api.post(`/admin/vouchers/review/${item.id}/resolve`,
                     { note: note.trim(), actions });
      toast.success("Resolved. Audit row appended; nothing was deducted.");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't resolve the item.");
    } finally { setBusy(false); }
  };

  const acknowledge = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/vouchers/review/${item.id}/acknowledge`, {});
      toast.success("Filed. Nothing was decided and nothing moved.");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't file it.");
    } finally { setBusy(false); }
  };

  const snap = detail?.snapshot;
  return (
    <div className={solo ? "" : "border hairline rounded bg-white p-3"}
         data-testid={`review-item-${item.id}`}>
      {!solo && (
        <div className="text-[10px] uppercase tracking-[0.16em] text-[#6B6A68] mb-1">
          {KIND_LABEL[item.kind] || item.kind} · {new Date(item.created_at).toLocaleString()}
        </div>
      )}
      {!solo && (
        <div className="text-[11px] text-[#3A3936] leading-snug mb-2">{moneyStory(item)}</div>
      )}
      <div className="text-[10px] text-[#2F6B3A] mb-2" data-testid={`review-already-${item.id}`}>
        Already done automatically: {alreadyDone(item.kind)}
      </div>

      {isOpen && item.band === "record" && (
        <button
          type="button" onClick={acknowledge} disabled={busy}
          data-testid={`review-acknowledge-${item.id}`}
          className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 rounded border border-[#4A5D4E] bg-[#EEF3EE] text-[#2F4A33] hover:bg-[#E4EDE5] disabled:opacity-50"
        >
          <CheckCircle size={12} weight="bold" />
          {busy ? "Filing…" : "Acknowledge"}
        </button>
      )}

      {isOpen && item.band === "clock" && (
        <div className="flex flex-wrap items-center gap-2 mb-2">
          {dashboardBase && (
            <a href={`${dashboardBase}/disputes${item.dispute_id ? `/${item.dispute_id}` : ""}`}
               target="_blank" rel="noreferrer"
               data-testid={`review-stripe-dispute-${item.id}`}
               className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 rounded border border-[#A89360] text-[#7A5C20] hover:bg-[#FAF2DA]">
              Answer it in Stripe <ArrowSquareOut size={11} weight="bold" />
            </a>
          )}
          {item.stripe_charge_id && (
            <button
              type="button" onClick={() => onRefundCharge?.(item.stripe_charge_id)}
              data-testid={`review-refund-instead-${item.id}`}
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 rounded border border-[#C16E5A] text-[#7A3A2A] hover:bg-[#FDF1ED]"
            >
              <ArrowUUpLeft size={11} weight="bold" /> Refund instead of fighting it
            </button>
          )}
        </div>
      )}

      <details className="mt-2" data-testid={`review-technical-${item.id}`} onToggle={loadDetail}>
        <summary className="text-[10px] uppercase tracking-[0.16em] text-[#6B6A68] cursor-pointer">
          Technical details
        </summary>
        <div className="mt-2 space-y-2">
          <div className="text-[11px] text-[#3A3936] leading-snug">{item.detail}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-[10px] font-mono text-[#6B6A68]">
            <div data-testid={`rv-charge-${item.id}`}>charge · {item.stripe_charge_id || "—"}</div>
            <div>dispute · {item.dispute_id || "—"}</div>
            <div>purchase · {detail?.purchase?.id || "—"}</div>
            <div>paymentintent · {detail?.purchase?.stripe_payment_intent_id || "—"}</div>
            <div>creator · {detail?.creator?.email || item.user_id || "—"}</div>
            <div>item · {item.id}</div>
          </div>
          {snap && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5 text-[10px]">
              {[["Paid", snap.paid_cents], ["Consumed", snap.consumed_cents],
                ["Unused", snap.unused_cents], ["Frozen", snap.frozen_cents],
                ["Reversed", snap.reversed_cents], ["Already refunded", snap.already_refunded_cents]]
                .map(([l, v]) => (
                  <div key={l} className="bg-white border hairline rounded px-2 py-1">
                    <div className="text-[9px] uppercase tracking-[0.14em] text-[#6B6A68]">{l}</div>
                    <div className="tabular-nums">{fmt$(v)}</div>
                  </div>
                ))}
            </div>
          )}
          {(detail?.lots || []).length > 0 && (
            <div className="border hairline rounded bg-white overflow-hidden" data-testid={`rv-lots-${item.id}`}>
              {detail.lots.map((l) => (
                <div key={l.id} className="px-2 py-1.5 flex items-center gap-2 text-[10px] border-b hairline last:border-0">
                  <span className="font-mono text-[#6B6A68]">{l.id.slice(0, 8)}</span>
                  <span className="text-[#1C1B1A]">{l.provider}</span>
                  <span className="ml-auto tabular-nums">orig {fmt$(l.original_cents)}</span>
                  <span className="tabular-nums text-[#2F6B3A]">act {fmt$(l.active_cents)}</span>
                  <span className="tabular-nums text-[#8A5A00]">frz {fmt$(l.frozen_cents)}</span>
                  <span className="tabular-nums text-[#7A3A2A]">rev {fmt$(l.reversed_cents)}</span>
                </div>
              ))}
            </div>
          )}
          {(detail?.audit || []).length > 0 && (
            <div className="border hairline rounded bg-white max-h-48 overflow-auto" data-testid={`rv-audit-${item.id}`}>
              {detail.audit.map((a) => (
                <div key={a.id} className="px-2 py-1 text-[10px] flex items-center gap-2 border-b hairline last:border-0">
                  <span className="text-[#6B6A68] shrink-0">{new Date(a.created_at).toLocaleString()}</span>
                  <span className="text-[#1C1B1A]">{a.event}</span>
                  <span className="tabular-nums ml-auto">{fmt$(a.amount_cents)}</span>
                  <span className="text-[#6B6A68] truncate max-w-[40%]">{a.reason}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </details>

      {isOpen ? (
        <div className="border-t hairline mt-3 pt-3">
          <div className="text-[9px] uppercase tracking-[0.16em] text-[#6B6A68] mb-1.5">
            {item.band === "record"
              ? "Or close it with a decision of your own"
              : "Your decision — no option deducts money or credit automatically"}
          </div>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {Object.keys(ACTION_LABEL).map((a) => (
              <button
                key={a} type="button" onClick={() => toggleAction(a)}
                data-testid={`rv-action-${a}`}
                className={`text-[10px] px-2 py-1 rounded border transition-colors ${
                  actions.includes(a) ? "border-[#4A5D4E] bg-[#EEF3EE] text-[#2F4A33]"
                                      : "border-[#D8D6D2] text-[#6B6A68] hover:border-[#A89360]"}`}
              >
                {ACTION_LABEL[a]}
              </button>
            ))}
          </div>
          <textarea
            value={note} onChange={(e) => setNote(e.target.value)} rows={2}
            placeholder="What you decided and why. Required — it is written to the audit trail."
            data-testid={`rv-note-${item.id}`}
            className="w-full text-[11px] border hairline rounded p-2 bg-white"
          />
          <button
            type="button" onClick={resolve} disabled={busy}
            data-testid={`rv-resolve-${item.id}`}
            className="mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 rounded bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50"
          >
            <CheckCircle size={12} weight="bold" />
            {busy ? "Resolving…" : "Resolve"}
          </button>
        </div>
      ) : (
        <div className="border-t hairline mt-3 pt-3 text-[11px] text-[#3A3936]"
             data-testid={`rv-resolved-${item.id}`}>
          <div className="text-[9px] uppercase tracking-[0.16em] text-[#6B6A68] mb-1">
            Resolved by {item.resolved_by} ·{" "}
            {item.resolved_at && new Date(item.resolved_at).toLocaleString()}
          </div>
          {(item.resolution_actions || []).map((a) => (
            <span key={a} className="inline-block mr-1.5 mb-1 text-[10px] px-2 py-0.5 rounded border border-[#B7CBB9] bg-[#F1F7F1] text-[#2F6B3A]">
              {ACTION_LABEL[a] || a}
            </span>
          ))}
          <div className="mt-1 italic">{item.resolution_note}</div>
        </div>
      )}
    </div>
  );
}
